// Copyright (c) 2019 Comsearch, Inc., All rights reserved

import { AnalysisDisplayColorIdEnum } from 'config/enums'
import { error } from 'react-notification-system-redux'
import { extractAnalysisResults } from 'redux/modules/interferenceAnalysis/helpers'

// takes the analysis results from the API and separates out
// 'groupedFrequencyPlans' - the grid structure: which interferenceAnalysis redux will store
// and 'gridInitialValues' - all path.channel.polarizationStatus rolled up and sent to redux-form

const parseAnalysisResults = results => (dispatch, getState) => {
  const pathsFromState = getState().projectSummary.get('paths')

  const gridInitialValues = {}
  const groupedFrequencyPlans = []

  let includesUnsetColorId = false

  results.groupedFrequencyPlans.forEach(r => {
    // make a list of paths data which we will store in interferenceAnalysis redux
    // this paths data will hold any values related to analysis grid (results)
    // which do not belong in redux form.
    const paths = r.paths.reduce((a, d) => {
      // we assign the index for display purposes. The interference analysis API data
      // does not know about this numbering so we take it from projectSummary data.
      const pathIndex = pathsFromState.find(p => p.get('pathId') === d.pathId).get('pathIndex')

      // separate these values out from the path data
      const {
        afsDisplayAlgorithmResults,
        ...pathSummary
      } = d

      const {
        // analysisResults - goes to interferenceAnalysis redux state
        // so we can display the grid info (status colors, adjacent buck, filtered etc)
        analysisResults,
        // channelPairs - goes to AFS_GRID_RESULTS redux-form so the user can select
        // what they want to do with the polarizations.
        channelPairs,
      } = extractAnalysisResults(afsDisplayAlgorithmResults, r.pairedFrequencyHeadings.length)

      // check to see if any of the results have UNSET as their analysis status.
      analysisResults.forEach(ddd => {
        if (
          ddd.colorH === AnalysisDisplayColorIdEnum.UNSET ||
          ddd.colorV === AnalysisDisplayColorIdEnum.UNSET ||
          ddd.filteredColorH === AnalysisDisplayColorIdEnum.UNSET ||
          ddd.filteredColorV === AnalysisDisplayColorIdEnum.UNSET
        ) {
          includesUnsetColorId = true
        }
      })

      // store the afsDisplayAlgorithmResults for redux form.
      // NOTE - prefix the path_id_ because when trying to track
      // touched for the input fields, redux-form on V8 will make
      // multiple HUGE arrays since path ids are in the millions.
      gridInitialValues[`path_id_${d.pathId}`] = channelPairs

      // the rest of the path info is going into interferenceAnalysis redux state
      // since it has no purpose being inside of redux form.
      a.push({
        ...pathSummary,
        analysisResults,
        pathIndex,
      })

      return a
    }, [])

    // LEGO-10662 - hide low power frequency warning from UI
    // remove frequency warning for lowPower because users only care about this warning elsewhere in the workflow.
    r.pairedFrequencyHeadings.forEach(pairedFrequencyHeading => {
      delete pairedFrequencyHeading.pairedFrequencyWarnings.lowPower
    })

    groupedFrequencyPlans.push({
      pairedFrequencyHeadings: r.pairedFrequencyHeadings,
      planSummary: r.planSummary,
      paths,
    })
  })

  if (includesUnsetColorId) {
    dispatch(error({
      title: 'Analysis Results Rendering Error',
      message: 'Invalid Analysis results were returned. Please reload the page for correct results.',
      dismissible: false,
      autoDismiss: 0,
      action: {
        label: 'Reload page.',
        callback: () => window.location.reload(),
      },
    }))
  }

  return {
    gridInitialValues,
    groupedFrequencyPlans,
  }
}

export default parseAnalysisResults
